<script lang="ts">
  /**
   * The focus takeover: the whole of AF1's "work on this one item" step, in one
   * surface.
   *
   * It authors **nothing** durable. Every effect goes out through a callback the
   * host implements, which is what makes the session-cell write protocol
   * (STEP_5_SPEC.md §4) checkable without a store at all:
   *
   *     [Finished]  → FinishTask          → { current_task_id: null }
   *     [Re-enter]  → verbatim re-entry at once for a non-split task; the sheet
 *                   for a split one — a task that is itself a piece of a
 *                   breakdown (idea 12). The sheet commits through the host.
   *     [Step back] → nothing              → { current_task_id: null, status: 'Paused' }
   *
   * `status` stays `'Active'` after a commit, because AF1 continues the same page
   * sweep — only an explicit step-back and the 4-hour timeout change it.
   * `lamport` and `last_activity_at` belong to `updateSession` and nothing here
   * sets them.
   *
   * Exactly three actions, and `Esc` is the keyboard equivalent of step-back. No
   * `[Pause]` button: Android needs one because a phone has no `Esc` key.
   *
   * It renders as a **sibling** of the page stage, never inside it, or the
   * `inert` the host puts on the stage would disable the modal too (D4).
   */
  import type { NotebookStore } from '../storage/operations';
  import type { Task } from '../types/generated';
  import { splitTitleLink } from '../utils/taskStats';
  import Icon from './Icon.svelte';
  import ArrowLeft from 'lucide/dist/esm/icons/arrow-left.mjs';
  import Check from 'lucide/dist/esm/icons/check.mjs';
  import RotateCcw from 'lucide/dist/esm/icons/rotate-ccw.mjs';
  import X from 'lucide/dist/esm/icons/x.mjs';
  import InFlightQuickCapture from './InFlightQuickCapture.svelte';
  import TomatoTimer from './TomatoTimer.svelte';
  import { TOMATO_DURATION_MS } from '../config';

  interface Props {
    store: NotebookStore;
    /** The task in flight. */
    task: Task;
    pageNumber: number;
    /** Server verdicts to surface as a dismissible notice (§0.3). */
    rejections?: ReadonlyArray<{ op_id: string; reason: string }>;
    onFinished: () => void | Promise<void>;
    onReenter: () => void;
    onStepBack: () => void | Promise<void>;
    /** Lets the host clear the notice it owns. Optional: it falls back to local. */
    onDismissNotice?: () => void;
    /** Step 7 (§D4): the takeover's docked capture routes through the host too. */
    onCaptureToNotebook?: (tag: string, title: string) => void;
    onCreateNotebook?: (tag: string, title: string) => void;
    /** IDEAS 15: the host's route table, for the docked capture's suggestions. */
    notebookTags?: ReadonlyArray<{ tag: string; name: string }>;
    /**
     * R4.6: the host's last-captured memory. `e` re-opens the dock prefilled as
     * an edit of it — guarded on the notebook it lives in, because the reword
     * goes through this takeover's store.
     */
    lastCaptured?: { taskId: string; title: string; notebookId: string } | null;
    /** R4.6: the dock's landed captures (append or reword) report to the host. */
    onCommitted?: (taskId: string, title: string) => void;
  }

  let {
    store,
    task,
    pageNumber,
    rejections = [],
    onFinished,
    onReenter,
    onStepBack,
    onDismissNotice,
    onCaptureToNotebook,
    onCreateNotebook,
    notebookTags,
    lastCaptured = null,
    onCommitted,
  }: Props = $props();

  /** R4.6: the edit in flight — `e` moves the memory here, the dock's close clears it. */
  let editTarget = $state<{ taskId: string; title: string } | null>(null);

  /**
   * `false` hides the notice for this mount without telling the host.
   *
   * The host owns the list — it is drained from the store, not from here — so
   * this is only the fallback for a caller that passed no handler.
   */
  let noticeDismissed = $state(false);

  /** Newest first: a single notice, not a stack. */
  const notice = $derived(
    noticeDismissed || rejections.length === 0 ? null : rejections[rejections.length - 1]!,
  );

  /** R5.6: the title's URL, split at render time — presentation only. */
  const link = $derived(splitTitleLink(task.title));

  /**
   * Ruling 2026-10-16: the re-enter choice silences an elapsed tomato while
   * the new items are typed. Both [ Re-enter ] buttons funnel through here;
   * the mute is one-way — a cancelled sheet does not resume the ringing, the
   * same persistence as the prompt's own [ Stop ].
   */
  let chimeMuted = $state(false);

  function chooseReenter() {
    chimeMuted = true;
    onReenter();
  }

  function dismissNotice() {
    noticeDismissed = true;
    onDismissNotice?.();
  }

  /**
   * The takeover has one input — the docked capture. Keys struck inside it are
   * text, not commands, which is what the R4.6 `e` branch below leans on.
   */
  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
  }

  function onkeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      void onStepBack();
      return;
    }
    // R4.6: edit the last stray — the dock re-opens prefilled and Enter rewords
    // instead of appending. Within reach only, and keys in the field are text.
    if (isTypingTarget(event.target)) return;
    if (!lastCaptured || lastCaptured.notebookId !== store.identity.notebook_id) return;
    if (event.key === 'e') {
      event.preventDefault();
      editTarget = lastCaptured;
    }
  }

  /**
   * The dialog takes focus on open — the capture drawer's rule, and the same
   * bug class: without it, focus stayed on `<body>` and the modal was a
   * bystander in the accessibility tree. The root is `tabindex="-1"`, so this
   * is the dialog pattern's programmatic focus, not a tab stop.
   */
  let rootEl = $state<HTMLElement | undefined>(undefined);
  $effect(() => {
    rootEl?.focus();
  });
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  class="takeover"
  data-testid="focus-takeover"
  data-mode="working"
  data-task-id={task.task_id}
  data-page-number={pageNumber}
  role="dialog"
  aria-modal="true"
  aria-label="Working on {task.title}"
  tabindex="-1"
  bind:this={rootEl}
  {onkeydown}
>
  {#if notice}
    <div class="takeover-notice" data-testid="focus-takeover-notice" aria-live="polite">
      <!--
        The verdict's own reason, not "something went wrong". A rejection freezes
        the user's intent, so the user has to be told what was refused and why —
        the same single-toast presentation the relayout notice already uses
        (REPLICATION.md §6.3). Silence is the goal in the common case.
      -->
      <span class="takeover-notice-reason">{notice.reason}</span>
      <button class="action" type="button" onclick={dismissNotice}>
        <Icon node={X} />
        Dismiss
      </button>
    </div>
  {/if}

  <p class="takeover-eyebrow">Page {pageNumber} — working on this</p>

  <!-- R5.6: same bracketed link as the ruled line — href is the URL verbatim,
       full address on the tooltip, pointer-only tabindex. No stopPropagation
       needed: the takeover routes clicks nowhere. -->
  <h2 class="takeover-title" data-testid="focus-takeover-title"
    >{#if link}{link.before}<a
      class="title-link"
      href={link.url}
      title={link.url}
      rel="noopener noreferrer"
      target="_blank"
      tabindex="-1">[link]</a
    >{link.after}{:else}{task.title}{/if}</h2
  >

  <!--
    `onElapsed` is deliberately NOT wired to `onReenter`. D7 is explicit that
    expiry changes no session field and authors nothing, and that choosing what
    happens next is the user's — so reaching zero must not open the sheet for
    them. The timer renders the prompt; the two buttons below are its only exits.

    Ruling 2026-10-16: choosing re-entry — by EITHER button — also silences an
    elapsed tomato's alarm while the new items are typed. One funnel, one mute;
    a cancelled sheet does not resume the ringing (same persistence as [Stop]).
  -->
  <TomatoTimer
    durationMs={TOMATO_DURATION_MS}
    onReenter={chooseReenter}
    onFinished={() => void onFinished()}
    muted={chimeMuted}
  />

  <!-- A stray thought while working goes to the Final page, same as the drawer. -->
  <InFlightQuickCapture
    {store}
    placeholder="Capture a stray thought"
    editing={editTarget}
    {onCommitted}
    onClose={() => {
      editTarget = null;
      // R4.6: the field's close blurs it — the dialog takes focus back, so `e`
      // is strikable right after a capture instead of typing into the field.
      rootEl?.focus();
    }}
    onCaptureToNotebook={onCaptureToNotebook}
    onCreateNotebook={onCreateNotebook}
    {notebookTags}
  />

  <div class="takeover-actions">
    <button class="action" type="button" data-testid="focus-takeover-finish" onclick={() => void onFinished()}>
      <Icon node={Check} />
      Finished
    </button>
    <button class="action action--dismiss" type="button" data-testid="focus-takeover-reenter" onclick={chooseReenter}>
      <Icon node={RotateCcw} />
      Re-enter
    </button>
    <button class="action" type="button" data-testid="focus-takeover-back" onclick={() => void onStepBack()}>
      <Icon node={ArrowLeft} />
      Step back
    </button>
  </div>
</div>

<style>
  .takeover {
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 32px 16px;
    font-family: var(--font-ui);
    color: var(--ink);
  }

  /* R5.2: the surface and its type key off the working mode, not the plain
     class — this is the one presentation a takeover in flight may wear. The
     tint is opaque: the stage beneath is already blurred and inert, so this is
     the only readable surface while a task is in flight. */
  .takeover[data-mode='working'] {
    background: var(--paper-working);
  }

  .takeover-eyebrow {
    margin: 0;
    font-size: 15px;
    color: var(--accent);
  }

  /* The task title is the writing face and the largest thing on screen: it is
     the one thing the user is here for. R5.2 scales it further — this is the
     mode's headline, not another line of the page. */
  .takeover-title {
    margin: 0;
    font-family: var(--font-hand);
    font-size: calc(var(--hand-size) * 2.75);
    font-weight: 400;
    text-align: center;
    max-width: 20em;
  }

  .takeover-actions {
    display: flex;
    gap: 10px;
  }

  .takeover-notice {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border: 1px solid color-mix(in srgb, var(--notice-border) 34%, transparent);
    border-radius: 3px;
    background: var(--notice-surface);
    font-size: 12px;
  }

  .takeover-notice-reason {
    font-family: var(--font-ui);
    letter-spacing: 0.02em;
  }

  @media (prefers-reduced-motion: reduce) {
    .takeover {
      transition: none;
    }
  }
</style>
